import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSubscription } from 'urql';
import { api } from '../lib/api';
import { getErrorMessage } from '../lib/error-message';
import { graphql } from '../lib/graphql';
import { useOrg } from '../hooks/use-org';
import { useAuth } from './auth';

const MyAgentStatusSubscription = graphql(`
  subscription MyAgentStatus($organizationId: Uuid!, $userId: Uuid!) {
    agentStatus(where: { organizationId: { _eq: $organizationId }, member: { userId: { _eq: $userId } } }) {
      id
      statusName
      statusChangedAt
      softphoneConnected
      returnStatusName
      currentAgentCallSessionId
      currentCallControlId
      currentQueueEntryId
      wrapUpEndsAt
      lastHeartbeat
      memberId
    }
  }
`);

const AgentStatusDefinitionsSubscription = graphql(`
  subscription AgentStatusDefinitions($organizationId: Uuid!) {
    agentStatusDefinitions(where: { organizationId: { _eq: $organizationId } }, orderBy: { displayOrder: ASC }) {
      name
      label
      color
      availableForRouting
      displayOrder
      isSystem
    }
  }
`);

type TelnyxRTCInstance = {
  remoteElement: string;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  connect: () => void;
  disconnect: () => void;
  newCall: (options: { destinationNumber: string; callerNumber: string }) => TelnyxCall;
};

type TelnyxCall = {
  id?: string;
  state?: string;
  direction?: string;
  remoteCallerName?: string;
  remoteCallerNumber?: string;
  isAudioMuted?: boolean;
  customHeaders?: Array<{ name: string; value: string }>;
  options?: {
    telnyxCallControlId?: string;
    telnyxSessionId?: string;
    telnyxLegId?: string;
  };
  telnyxIDs?: {
    telnyxCallControlId?: string;
    telnyxSessionId?: string;
    telnyxLegId?: string;
  };
  answer: () => void;
  hangup: () => void;
  toggleAudioMute: () => void;
  toggleHold: () => void;
  dtmf: (digits: string) => void;
  setAudioInDevice: (deviceId: string) => void;
  setAudioOutDevice: (deviceId: string) => void;
};

type CallState = {
  id: string;
  callControlId: string | null;
  direction: 'inbound' | 'outbound';
  state: 'ringing' | 'active' | 'held' | 'done';
  remoteCallerName: string | null;
  remoteCallerNumber: string | null;
  isMuted: boolean;
  isHeld: boolean;
  startedAt: number;
  queueEntryId: string | null;
  sessionVariables: Record<string, string> | null;
};

type SoftphoneContextType = {
  isConnected: boolean;
  isConnecting: boolean;
  activeCall: CallState | null;
  lastError: string | null;
  agentStatus: string | null;
  statusLocked: boolean;
  wrapUpEndsAt: number | null;
  statusDefinitions: Array<{ name: string; label: string; color: string; availableForRouting: boolean }>;
  connect: (initialStatusName?: string) => Promise<void>;
  disconnect: () => void;
  makeCall: (destinationNumber: string, callerNumber: string) => void;
  answerCall: () => void;
  rejectCall: () => void;
  hangupCall: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  sendDtmf: (digits: string) => void;
  setAgentStatus: (statusName: string) => Promise<void>;
  blindTransfer: (destination: string) => Promise<void>;
  endWrapUp: () => Promise<void>;
  clearLastError: () => void;
  audioInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  selectedAudioInput: string | null;
  selectedAudioOutput: string | null;
  setAudioInput: (deviceId: string) => void;
  setAudioOutput: (deviceId: string) => void;
};

export const SoftphoneContext = createContext<SoftphoneContextType>({
  isConnected: false,
  isConnecting: false,
  activeCall: null,
  lastError: null,
  agentStatus: null,
  statusLocked: false,
  wrapUpEndsAt: null,
  statusDefinitions: [],
  connect: async () => {},
  disconnect: () => {},
  makeCall: () => {},
  answerCall: () => {},
  rejectCall: () => {},
  hangupCall: () => {},
  toggleMute: () => {},
  toggleHold: () => {},
  sendDtmf: () => {},
  setAgentStatus: async () => {},
  blindTransfer: async () => {},
  endWrapUp: async () => {},
  clearLastError: () => {},
  audioInputDevices: [],
  audioOutputDevices: [],
  selectedAudioInput: null,
  selectedAudioOutput: null,
  setAudioInput: () => {},
  setAudioOutput: () => {},
});

const SOFTPHONE_DEBUG = process.env.BUN_PUBLIC_SOFTPHONE_DEBUG === '1';
const HEARTBEAT_INTERVAL_MS = 30_000;
const TERMINAL_CALL_TOMBSTONE_TTL_MS = 60_000;
const STATUS_LOCK_MESSAGE = 'Finish the current call before changing availability';

type SoftphoneCallSyncState = 'ringing' | 'active' | 'ended';

type CallLifecycleState = {
  queueEntryId: string | null;
  callControlId: string | null;
  telnyxCallId: string | null;
  telnyxSessionId: string | null;
  wasAnswered: boolean;
  lastSyncedState: SoftphoneCallSyncState | null;
};

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { organizationId } = useOrg();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [statusResult] = useSubscription({
    query: MyAgentStatusSubscription,
    variables: { organizationId: organizationId ?? '', userId: userId ?? '' },
    pause: !organizationId || !userId,
  });

  const [statusDefinitionsResult] = useSubscription({
    query: AgentStatusDefinitionsSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const myStatus = statusResult.data?.agentStatus[0] ?? null;

  const clientRef = useRef<TelnyxRTCInstance | null>(null);
  const callRef = useRef<TelnyxCall | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeCallRef = useRef<CallState | null>(null);
  const callLifecycleRef = useRef<Map<string, CallLifecycleState>>(new Map());
  const terminalCallTombstonesRef = useRef<Map<string, number>>(new Map());
  const statusRef = useRef<typeof myStatus>(null);
  const lastGatewayStateRef = useRef<string | null>(null);
  const lastSocketCloseRef = useRef<string | null>(null);
  const lastSocketErrorRef = useRef<string | null>(null);
  const autoAnsweredCallIdsRef = useRef<Set<string>>(new Set());

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [activeCall, setActiveCall] = useState<CallState | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string | null>(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string | null>(null);

  useEffect(() => {
    statusRef.current = myStatus ?? null;
  }, [myStatus]);

  const enumerateDevices = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((d) => d.kind === 'audioinput');
    const outputs = devices.filter((d) => d.kind === 'audiooutput');
    setAudioInputDevices(inputs);
    setAudioOutputDevices(outputs);
    setSelectedAudioInput(
      (prev) => prev ?? inputs.find((d) => d.deviceId === 'default')?.deviceId ?? inputs[0]?.deviceId ?? null,
    );
    setSelectedAudioOutput(
      (prev) => prev ?? outputs.find((d) => d.deviceId === 'default')?.deviceId ?? outputs[0]?.deviceId ?? null,
    );
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (!organizationId) return;

    heartbeatRef.current = setInterval(() => {
      const currentStatus = statusRef.current;

      void api['agent-status'].heartbeat
        .post({
          organizationId,
          hasActiveDirectCall: !!activeCallRef.current && !activeCallRef.current.queueEntryId,
          hasRecoveringDirectCall:
            !activeCallRef.current && !!currentStatus?.currentAgentCallSessionId && !currentStatus.currentQueueEntryId,
        })
        .catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
  }, [organizationId]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const setActiveCallState = useCallback((nextCall: CallState | null) => {
    activeCallRef.current = nextCall;
    setActiveCall(nextCall);
  }, []);

  const resetCallSession = useCallback(() => {
    setActiveCallState(null);
    callRef.current = null;
    callLifecycleRef.current.clear();
    autoAnsweredCallIdsRef.current.clear();
  }, [setActiveCallState]);

  const disposeClient = useCallback((client: TelnyxRTCInstance | null) => {
    if (!client) return;
    if (clientRef.current === client) {
      clientRef.current = null;
    }
    try {
      client.disconnect();
    } catch {}
  }, []);

  const syncSoftphoneConnection = useCallback(
    async (connected: boolean) => {
      if (!organizationId) return;

      const { error } = await api['agent-status'].softphone.post({ organizationId, connected });

      if (error) throw new Error(getErrorMessage(error, 'Unable to sync softphone connection'));
    },
    [organizationId],
  );

  const recordTerminalCallTombstone = useCallback(
    (args: { telnyxCallId: string | null; telnyxSessionId: string | null; callControlId: string | null }) => {
      const now = Date.now();
      pruneExpiredCallTombstones(terminalCallTombstonesRef.current, now);
      for (const key of buildCallTombstoneKeys(args.telnyxCallId, args.telnyxSessionId, args.callControlId)) {
        terminalCallTombstonesRef.current.set(key, now + TERMINAL_CALL_TOMBSTONE_TTL_MS);
      }
    },
    [],
  );

  const isTerminalCallTombstone = useCallback(
    (args: { telnyxCallId: string | null; telnyxSessionId: string | null; callControlId: string | null }) => {
      const now = Date.now();
      pruneExpiredCallTombstones(terminalCallTombstonesRef.current, now);
      return buildCallTombstoneKeys(args.telnyxCallId, args.telnyxSessionId, args.callControlId).some(
        (key) => (terminalCallTombstonesRef.current.get(key) ?? 0) > now,
      );
    },
    [],
  );

  const syncDirectCallState = useCallback(
    (args: {
      callId: string;
      callControlId: string | null;
      telnyxCallId?: string | null;
      telnyxSessionId?: string | null;
      queueEntryId: string | null;
      state: SoftphoneCallSyncState;
      direction?: 'inbound' | 'outbound';
      remoteCallerName?: string | null;
      remoteCallerNumber?: string | null;
      wasAnswered?: boolean;
    }) => {
      if (!organizationId || !args.callId || args.queueEntryId) return;

      const previous = callLifecycleRef.current.get(args.callId);
      if (previous?.lastSyncedState === 'ended') return;
      if (previous?.lastSyncedState === 'active' && args.state === 'ringing') return;
      const next: CallLifecycleState = {
        queueEntryId: args.queueEntryId,
        callControlId: args.callControlId ?? previous?.callControlId ?? null,
        telnyxCallId: args.telnyxCallId ?? previous?.telnyxCallId ?? null,
        telnyxSessionId: args.telnyxSessionId ?? previous?.telnyxSessionId ?? null,
        wasAnswered: previous?.wasAnswered || !!args.wasAnswered || args.state === 'active',
        lastSyncedState: args.state,
      };

      if (
        previous?.lastSyncedState === next.lastSyncedState &&
        previous.wasAnswered === next.wasAnswered &&
        previous.callControlId === next.callControlId &&
        previous.telnyxCallId === next.telnyxCallId &&
        previous.telnyxSessionId === next.telnyxSessionId &&
        previous.queueEntryId === next.queueEntryId
      ) {
        return;
      }

      callLifecycleRef.current.set(args.callId, next);
      void api['call-control']['sync-softphone-call-state']
        .post({
          organizationId,
          clientCallId: args.callId,
          telnyxCallId: next.telnyxCallId,
          telnyxSessionId: next.telnyxSessionId,
          callControlId: next.callControlId,
          state: args.state,
          direction: args.direction ?? null,
          remoteCallerName: args.remoteCallerName ?? null,
          remoteCallerNumber: args.remoteCallerNumber ?? null,
          queueEntryId: args.queueEntryId ?? null,
          wasAnswered: next.wasAnswered,
        })
        .then(({ error }) => {
          if (error) console.error('Softphone call sync failed:', serializeUnknownError(error));
        });
    },
    [organizationId],
  );

  const resolveConnectStatus = useCallback(
    (requestedStatusName?: string) => {
      if (myStatus?.currentAgentCallSessionId || myStatus?.currentCallControlId || myStatus?.currentQueueEntryId) {
        return null;
      }

      const preferredStatus =
        requestedStatusName ??
        (myStatus?.statusName && isUserSelectableStatus(myStatus.statusName) ? myStatus.statusName : 'available');

      return isUserSelectableStatus(preferredStatus) ? preferredStatus : 'available';
    },
    [
      myStatus?.currentAgentCallSessionId,
      myStatus?.currentCallControlId,
      myStatus?.currentQueueEntryId,
      myStatus?.statusName,
    ],
  );

  const connect = useCallback(
    async (initialStatusName: string = 'available') => {
      const allowRecoveredDirectCallReconnect = !!(
        isConnected &&
        myStatus?.currentAgentCallSessionId &&
        !myStatus.currentQueueEntryId &&
        !activeCallRef.current
      );

      if (!organizationId || isConnecting || (isConnected && !allowRecoveredDirectCallReconnect)) return;

      setIsConnecting(true);
      setLastError(null);
      lastGatewayStateRef.current = null;
      lastSocketCloseRef.current = null;
      lastSocketErrorRef.current = null;
      if (clientRef.current) {
        disposeClient(clientRef.current);
      }
      resetCallSession();
      const desiredStatus = resolveConnectStatus(initialStatusName);

      let client: TelnyxRTCInstance | null = null;
      await new Promise<void>(async (resolve, reject) => {
        let settled = false;

        const connectTimeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          if (client) disposeClient(client);
          stopHeartbeat();
          resetCallSession();
          setIsConnected(false);
          setIsConnecting(false);
          setLastError('Connection timed out');
          reject(new Error('Connection timed out'));
        }, 30_000);

        const resolveConnect = () => {
          if (settled) return;
          settled = true;
          clearTimeout(connectTimeoutId);
          resolve();
        };

        const rejectConnect = (message: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(connectTimeoutId);
          reject(new Error(message));
        };

        try {
          const tokenResult = await api.webrtc.token.post({ organizationId });

          if (tokenResult.error) {
            throw new Error(getErrorMessage(tokenResult.error, 'Unable to initialize the softphone'));
          }

          if (!audioRef.current) {
            audioRef.current = document.createElement('audio');
            audioRef.current.id = 'softphone-remote-audio';
            audioRef.current.autoplay = true;
            document.body.appendChild(audioRef.current);
          }

          const { TelnyxRTC } = await import('@telnyx/webrtc');
          client = new TelnyxRTC({
            login_token: tokenResult.data.token,
            debug: SOFTPHONE_DEBUG,
            debugOutput: 'socket',
          }) as unknown as TelnyxRTCInstance;

          client.remoteElement = 'softphone-remote-audio';

          client.on('telnyx.ready', () => {
            if (clientRef.current !== client) return;
            setIsConnected(true);
            setIsConnecting(false);
            setLastError(null);
            enumerateDevices();
            startHeartbeat();
            void (async () => {
              let syncErrorMessage: string | null = null;
              if (desiredStatus) {
                const { error } = await api['agent-status'].set.post({ organizationId, statusName: desiredStatus });

                if (error) {
                  console.error('Softphone status sync failed:', serializeUnknownError(error));
                  syncErrorMessage = getErrorMessage(error, 'Unable to sync softphone status');
                }
              }
              try {
                await syncSoftphoneConnection(true);
              } catch (error) {
                console.error('Softphone connection sync failed:', serializeUnknownError(error));
                syncErrorMessage ??= formatTelnyxError(error) ?? 'Unable to sync softphone connection';
              }
              if (syncErrorMessage) {
                setLastError(syncErrorMessage);
              }
              resolveConnect();
            })();
          });

          client.on('telnyx.error', (error: unknown) => {
            if (clientRef.current !== client) return;
            const details = serializeUnknownError(error);
            const message =
              formatTelnyxError(error, {
                gatewayState: lastGatewayStateRef.current,
                socketClose: lastSocketCloseRef.current,
                socketError: lastSocketErrorRef.current,
              }) ?? 'Unable to connect to Telnyx';
            console.error('Softphone Telnyx error:', details);
            void syncSoftphoneConnection(false).catch(() => {});
            disposeClient(client);
            stopHeartbeat();
            resetCallSession();
            setIsConnected(false);
            setIsConnecting(false);
            setLastError(message);
            rejectConnect(message);
          });

          client.on('telnyx.socket.error', (error: unknown) => {
            if (clientRef.current !== client) return;
            const details = serializeUnknownError(error);
            lastSocketErrorRef.current = compactErrorDetails(details);
            console.error('Softphone Telnyx socket error:', details);
          });

          client.on('telnyx.socket.close', (event: unknown) => {
            if (clientRef.current !== client) return;
            const closeReason = formatSocketClose(event);
            const closeMessage = closeReason ?? 'Telnyx socket closed';
            lastSocketCloseRef.current = closeReason;
            void syncSoftphoneConnection(false).catch(() => {});
            if (clientRef.current === client) {
              clientRef.current = null;
            }
            setIsConnected(false);
            stopHeartbeat();
            resetCallSession();
            setIsConnecting(false);
            setLastError(closeMessage);
            rejectConnect(closeMessage);
          });

          client.on('telnyx.socket.message', (message: unknown) => {
            if (clientRef.current !== client) return;
            const gatewayState = parseGatewayState(message);
            if (gatewayState) {
              lastGatewayStateRef.current = gatewayState;
              if (['UNREGED', 'NOREG', 'FAILED', 'FAIL_WAIT'].includes(gatewayState)) {
                console.error('Softphone Telnyx gateway state:', serializeUnknownError(message));
              }
            }
          });

          client.on('telnyx.notification', (notification: unknown) => {
            if (clientRef.current !== client) return;
            const n = notification as {
              type: string;
              call: TelnyxCall;
              errorMessage?: string;
              errorName?: string;
            };
            const notificationError = [n.errorName, n.errorMessage].filter(Boolean).join(': ');
            if (notificationError) {
              console.error('Softphone Telnyx notification error:', serializeUnknownError(notification));
              setLastError(notificationError);
            }
            if (n.type !== 'callUpdate') return;

            const call = n.call;
            const state = call.state;
            const failureMessage = getCallFailureMessage(call);
            const customHeaders = call.customHeaders;
            const queueEntryId = customHeaders?.find((h) => h.name === 'X-Queue-Entry-Id')?.value ?? null;
            const callControlId = getCallControlId(call);
            const telnyxCallId = call.id ?? null;
            const telnyxSessionId = getTelnyxSessionId(call);
            if (
              !queueEntryId &&
              (state === 'ringing' || state === 'active' || state === 'held') &&
              isTerminalCallTombstone({ telnyxCallId, telnyxSessionId, callControlId })
            ) {
              return;
            }
            const lifecycleMatch = findLifecycleMatch(
              callLifecycleRef.current,
              telnyxCallId,
              telnyxSessionId,
              callControlId,
              queueEntryId,
            );
            const existingLifecycle = lifecycleMatch?.lifecycle ?? null;
            const localCallId =
              lifecycleMatch?.callId ??
              (queueEntryId ? (telnyxCallId ?? callControlId ?? createLocalCallId()) : createLocalCallId());
            const currentCallMatches =
              !!activeCallRef.current &&
              activeCallRef.current.id === localCallId &&
              (!callControlId || activeCallRef.current.callControlId === callControlId);

            if (state === 'done' || state === 'destroy') {
              const terminalTelnyxCallId = telnyxCallId ?? existingLifecycle?.telnyxCallId ?? null;
              const terminalTelnyxSessionId = telnyxSessionId ?? existingLifecycle?.telnyxSessionId ?? null;
              const terminalCallControlId = existingLifecycle?.callControlId ?? callControlId;
              autoAnsweredCallIdsRef.current.delete(localCallId);
              if (failureMessage) {
                console.error('Softphone call failed:', failureMessage, call);
                setLastError(failureMessage);
              }
              if (!queueEntryId && (terminalTelnyxCallId || terminalTelnyxSessionId || terminalCallControlId)) {
                recordTerminalCallTombstone({
                  telnyxCallId: terminalTelnyxCallId,
                  telnyxSessionId: terminalTelnyxSessionId,
                  callControlId: terminalCallControlId,
                });
              }
              if (lifecycleMatch) {
                syncDirectCallState({
                  callId: localCallId,
                  callControlId: terminalCallControlId,
                  telnyxCallId: terminalTelnyxCallId,
                  telnyxSessionId: terminalTelnyxSessionId,
                  queueEntryId: existingLifecycle?.queueEntryId ?? queueEntryId,
                  state: 'ended',
                  direction: call.direction === 'inbound' ? 'inbound' : 'outbound',
                  remoteCallerName: call.remoteCallerName ?? null,
                  remoteCallerNumber: call.remoteCallerNumber ?? null,
                  wasAnswered:
                    existingLifecycle?.wasAnswered ??
                    (currentCallMatches ? activeCallRef.current?.state !== 'ringing' : false),
                });
                callLifecycleRef.current.delete(localCallId);
              }
              if (currentCallMatches) {
                setActiveCallState(null);
                callRef.current = null;
              }
              return;
            }

            if (state === 'ringing' || state === 'active' || state === 'held') {
              const rawVars = customHeaders?.find((h) => h.name === 'X-Session-Variables')?.value;
              const sessionVariables = rawVars ? tryParseJson(rawVars) : null;
              const previousCall = activeCallRef.current;
              const lifecycle: CallLifecycleState = {
                queueEntryId,
                callControlId: callControlId ?? existingLifecycle?.callControlId ?? null,
                telnyxCallId: telnyxCallId ?? existingLifecycle?.telnyxCallId ?? null,
                telnyxSessionId: telnyxSessionId ?? existingLifecycle?.telnyxSessionId ?? null,
                wasAnswered: existingLifecycle?.wasAnswered || state === 'active' || state === 'held',
                lastSyncedState: existingLifecycle?.lastSyncedState ?? null,
              };

              callLifecycleRef.current.set(localCallId, lifecycle);
              callRef.current = call;

              setActiveCallState({
                id: localCallId,
                callControlId: lifecycle.callControlId,
                direction: call.direction === 'inbound' ? 'inbound' : 'outbound',
                state: state as 'ringing' | 'active' | 'held',
                remoteCallerName: call.remoteCallerName ?? previousCall?.remoteCallerName ?? null,
                remoteCallerNumber: call.remoteCallerNumber ?? previousCall?.remoteCallerNumber ?? null,
                isMuted: !!call.isAudioMuted,
                isHeld: state === 'held',
                startedAt: previousCall?.id === localCallId ? previousCall.startedAt : Date.now(),
                queueEntryId:
                  previousCall?.id === localCallId ? (previousCall.queueEntryId ?? queueEntryId) : queueEntryId,
                sessionVariables:
                  previousCall?.id === localCallId
                    ? (previousCall.sessionVariables ?? sessionVariables)
                    : sessionVariables,
              });

              syncDirectCallState({
                callId: localCallId,
                callControlId: lifecycle.callControlId,
                telnyxCallId: lifecycle.telnyxCallId,
                telnyxSessionId: lifecycle.telnyxSessionId,
                queueEntryId,
                state: state === 'ringing' ? 'ringing' : 'active',
                direction: call.direction === 'inbound' ? 'inbound' : 'outbound',
                remoteCallerName: call.remoteCallerName ?? null,
                remoteCallerNumber: call.remoteCallerNumber ?? null,
                wasAnswered: lifecycle.wasAnswered,
              });

              if (
                state === 'ringing' &&
                call.direction === 'inbound' &&
                shouldAutoAnswer(customHeaders) &&
                !autoAnsweredCallIdsRef.current.has(localCallId)
              ) {
                autoAnsweredCallIdsRef.current.add(localCallId);
                try {
                  call.answer();
                } catch (error) {
                  autoAnsweredCallIdsRef.current.delete(localCallId);
                  setLastError(formatTelnyxError(error) ?? 'Unable to auto-answer the call');
                }
              }
            }
          });

          clientRef.current = client;
          client.connect();
        } catch (error) {
          disposeClient(client);
          stopHeartbeat();
          resetCallSession();
          setIsConnecting(false);
          const message = formatTelnyxError(error) ?? 'Unable to initialize the softphone';
          setLastError(message);
          rejectConnect(message);
        }
      });
    },
    [
      organizationId,
      isConnecting,
      isConnected,
      myStatus?.currentAgentCallSessionId,
      myStatus?.currentQueueEntryId,
      enumerateDevices,
      startHeartbeat,
      stopHeartbeat,
      disposeClient,
      resetCallSession,
      resolveConnectStatus,
      isTerminalCallTombstone,
      recordTerminalCallTombstone,
      setActiveCallState,
      syncDirectCallState,
      syncSoftphoneConnection,
    ],
  );

  const disconnect = useCallback(() => {
    if (
      activeCallRef.current ||
      myStatus?.currentAgentCallSessionId ||
      myStatus?.currentCallControlId ||
      myStatus?.currentQueueEntryId
    ) {
      setLastError(STATUS_LOCK_MESSAGE);
      return;
    }

    stopHeartbeat();
    disposeClient(clientRef.current);
    if (audioRef.current) {
      audioRef.current.remove();
      audioRef.current = null;
    }
    setIsConnected(false);
    resetCallSession();
    setLastError(null);
    lastGatewayStateRef.current = null;
    lastSocketCloseRef.current = null;
    lastSocketErrorRef.current = null;
    if (organizationId) {
      void api['agent-status'].set.post({ organizationId, statusName: 'offline' });
      void api['agent-status'].softphone.post({ organizationId, connected: false });
    }
  }, [
    organizationId,
    myStatus?.currentAgentCallSessionId,
    myStatus?.currentCallControlId,
    myStatus?.currentQueueEntryId,
    stopHeartbeat,
    disposeClient,
    resetCallSession,
  ]);

  const makeCall = useCallback(
    (destinationNumber: string, callerNumber: string) => {
      if (!clientRef.current) return;
      if (
        activeCallRef.current ||
        myStatus?.currentAgentCallSessionId ||
        myStatus?.currentCallControlId ||
        myStatus?.currentQueueEntryId
      ) {
        setLastError(STATUS_LOCK_MESSAGE);
        return;
      }
      setLastError(null);
      const provisionalCallId = createLocalCallId();
      const startedAt = Date.now();
      callLifecycleRef.current.set(provisionalCallId, {
        queueEntryId: null,
        callControlId: null,
        telnyxCallId: null,
        telnyxSessionId: null,
        wasAnswered: false,
        lastSyncedState: null,
      });
      setActiveCallState({
        id: provisionalCallId,
        callControlId: null,
        direction: 'outbound',
        state: 'ringing',
        remoteCallerName: null,
        remoteCallerNumber: destinationNumber,
        isMuted: false,
        isHeld: false,
        startedAt,
        queueEntryId: null,
        sessionVariables: null,
      });
      syncDirectCallState({
        callId: provisionalCallId,
        callControlId: null,
        queueEntryId: null,
        state: 'ringing',
        direction: 'outbound',
        remoteCallerNumber: destinationNumber,
      });

      try {
        const call = clientRef.current.newCall({
          destinationNumber,
          callerNumber,
        });
        callRef.current = call;
        const telnyxCallId = call.id ?? null;
        const telnyxSessionId = getTelnyxSessionId(call);
        const callControlId = getCallControlId(call);
        const provisionalLifecycle = callLifecycleRef.current.get(provisionalCallId);
        if (provisionalLifecycle) {
          callLifecycleRef.current.set(provisionalCallId, {
            ...provisionalLifecycle,
            callControlId,
            telnyxCallId,
            telnyxSessionId,
          });
        }
        if (callControlId || telnyxCallId || telnyxSessionId) {
          syncDirectCallState({
            callId: provisionalCallId,
            callControlId,
            telnyxCallId,
            telnyxSessionId,
            queueEntryId: null,
            state: 'ringing',
            direction: 'outbound',
            remoteCallerNumber: destinationNumber,
          });
        }
      } catch (error) {
        syncDirectCallState({
          callId: provisionalCallId,
          callControlId: null,
          queueEntryId: null,
          state: 'ended',
          direction: 'outbound',
          remoteCallerNumber: destinationNumber,
          wasAnswered: false,
        });
        callLifecycleRef.current.delete(provisionalCallId);
        setActiveCallState(null);
        setLastError(formatTelnyxError(error) ?? 'Unable to start the call');
      }
    },
    [
      myStatus?.currentAgentCallSessionId,
      myStatus?.currentCallControlId,
      myStatus?.currentQueueEntryId,
      setActiveCallState,
      syncDirectCallState,
    ],
  );

  const answerCall = useCallback(() => {
    callRef.current?.answer();
  }, []);

  const rejectCall = useCallback(() => {
    callRef.current?.hangup();
  }, []);

  const hangupCall = useCallback(() => {
    if (callRef.current) {
      callRef.current.hangup();
      return;
    }

    const callControlId = activeCallRef.current?.callControlId ?? myStatus?.currentCallControlId;
    if (!organizationId || !myStatus?.currentAgentCallSessionId || myStatus.currentQueueEntryId) {
      return;
    }
    if (!callControlId) {
      setLastError('Remote hangup is unavailable until the call fully reconnects');
      return;
    }

    void api['call-control']['end-call']
      .post({
        organizationId,
        callControlId,
        sessionId: myStatus.currentAgentCallSessionId,
      })
      .then(({ error }) => {
        if (error) setLastError(getErrorMessage(error, 'Unable to end the call'));
      });
  }, [
    organizationId,
    myStatus?.currentAgentCallSessionId,
    myStatus?.currentCallControlId,
    myStatus?.currentQueueEntryId,
  ]);

  const toggleMute = useCallback(() => {
    callRef.current?.toggleAudioMute();
  }, []);

  const toggleHold = useCallback(() => {
    callRef.current?.toggleHold();
  }, []);

  const sendDtmf = useCallback((digits: string) => {
    callRef.current?.dtmf(digits);
  }, []);

  const setAgentStatus = useCallback(
    async (statusName: string) => {
      if (!organizationId) return;
      if (
        activeCallRef.current ||
        myStatus?.currentAgentCallSessionId ||
        myStatus?.currentCallControlId ||
        myStatus?.currentQueueEntryId
      ) {
        setLastError(STATUS_LOCK_MESSAGE);
        return;
      }

      const { error } = await api['agent-status'].set.post({ organizationId, statusName });

      if (error) throw new Error(getErrorMessage(error, 'Unable to update your status'));
    },
    [
      organizationId,
      myStatus?.currentAgentCallSessionId,
      myStatus?.currentCallControlId,
      myStatus?.currentQueueEntryId,
    ],
  );

  const setAudioInput = useCallback((deviceId: string) => {
    setSelectedAudioInput(deviceId);
    callRef.current?.setAudioInDevice(deviceId);
  }, []);

  const setAudioOutput = useCallback((deviceId: string) => {
    setSelectedAudioOutput(deviceId);
    callRef.current?.setAudioOutDevice(deviceId);
  }, []);

  const blindTransfer = useCallback(
    async (destination: string) => {
      if (!organizationId || !activeCall) return;
      if (!activeCall.callControlId) {
        setLastError('Call control is not available for this transfer');
        return;
      }

      const { error } = await api['call-control']['blind-transfer'].post({
        organizationId,
        callControlId: activeCall.callControlId,
        destination,
      });

      if (error) throw new Error(getErrorMessage(error, 'Unable to transfer the call'));
    },
    [organizationId, activeCall],
  );

  const endWrapUp = useCallback(async () => {
    if (!organizationId) return;

    const { error } = await api['call-control']['end-wrap-up'].post({ organizationId });

    if (error) throw new Error(getErrorMessage(error, 'Unable to end wrap-up'));
  }, [organizationId]);

  const clearLastError = useCallback(() => {
    setLastError(null);
  }, []);

  useEffect(() => {
    const autoAnsweredCallIds = autoAnsweredCallIdsRef.current;
    const terminalCallTombstones = terminalCallTombstonesRef.current;
    return () => {
      stopHeartbeat();
      disposeClient(clientRef.current);
      if (audioRef.current) {
        audioRef.current.remove();
        audioRef.current = null;
      }
      autoAnsweredCallIds.clear();
      terminalCallTombstones.clear();
    };
  }, [stopHeartbeat, disposeClient]);

  useEffect(() => {
    if (!isConnected || !organizationId) return;

    const interval = setInterval(() => {
      const currentCall = activeCallRef.current;
      if (!currentCall || currentCall.queueEntryId || !currentCall.id) return;

      const lifecycle = callLifecycleRef.current.get(currentCall.id);
      syncDirectCallState({
        callId: currentCall.id,
        telnyxCallId: lifecycle?.telnyxCallId ?? null,
        telnyxSessionId: lifecycle?.telnyxSessionId ?? null,
        callControlId: lifecycle?.callControlId ?? currentCall.callControlId,
        queueEntryId: null,
        state: currentCall.state === 'ringing' ? 'ringing' : 'active',
        direction: currentCall.direction,
        remoteCallerName: currentCall.remoteCallerName,
        remoteCallerNumber: currentCall.remoteCallerNumber,
        wasAnswered: lifecycle?.wasAnswered ?? currentCall.state !== 'ringing',
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isConnected, organizationId, syncDirectCallState]);

  const userSelectableStatuses = (statusDefinitionsResult.data?.agentStatusDefinitions ?? [])
    .filter((s) => !['offline', 'on_call', 'ringing', 'wrap_up'].includes(s.name))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((s) => ({ name: s.name, label: s.label, color: s.color, availableForRouting: s.availableForRouting }));
  const statusLocked = !!(
    activeCall ||
    myStatus?.currentAgentCallSessionId ||
    myStatus?.currentCallControlId ||
    myStatus?.currentQueueEntryId
  );

  const value: SoftphoneContextType = {
    isConnected,
    isConnecting,
    activeCall,
    lastError,
    agentStatus: myStatus?.statusName ?? null,
    statusLocked,
    wrapUpEndsAt: myStatus?.wrapUpEndsAt ? Date.parse(myStatus.wrapUpEndsAt) : null,
    statusDefinitions: userSelectableStatuses,
    connect,
    disconnect,
    makeCall,
    answerCall,
    rejectCall,
    hangupCall,
    toggleMute,
    toggleHold,
    sendDtmf,
    setAgentStatus,
    blindTransfer,
    endWrapUp,
    clearLastError,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioInput,
    selectedAudioOutput,
    setAudioInput,
    setAudioOutput,
  };

  return <SoftphoneContext value={value}>{children}</SoftphoneContext>;
}

function isUserSelectableStatus(statusName: string | null | undefined) {
  return !!statusName && !['offline', 'ringing', 'on_call', 'wrap_up'].includes(statusName);
}

function tryParseJson(raw: string): Record<string, string> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getCallFailureMessage(call: unknown) {
  if (!call || typeof call !== 'object') return null;

  const failedCall = call as {
    sipCode?: number;
    sipReason?: string;
    cause?: string;
  };

  if (typeof failedCall.sipCode === 'number' && failedCall.sipCode < 300) return null;
  if (failedCall.sipReason) {
    return typeof failedCall.sipCode === 'number'
      ? `SIP ${failedCall.sipCode}: ${failedCall.sipReason}`
      : failedCall.sipReason;
  }

  if (typeof failedCall.sipCode === 'number') {
    return `SIP ${failedCall.sipCode}`;
  }

  if (!failedCall.cause || failedCall.cause === 'NORMAL_CLEARING') return null;
  return failedCall.cause.replaceAll('_', ' ').toLowerCase();
}

function formatTelnyxError(
  error: unknown,
  context?: {
    gatewayState: string | null;
    socketClose: string | null;
    socketError: string | null;
  },
) {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const typedError = error as {
      message?: string;
      reason?: string;
      errorName?: string;
      errorMessage?: string;
      type?: string;
      error?: { message?: string; code?: string };
    };

    const message =
      typedError.errorMessage ??
      typedError.error?.message ??
      typedError.message ??
      typedError.reason ??
      [typedError.errorName, typedError.type].filter(Boolean).join(': ');

    if (message) return message;
  }

  if (context?.gatewayState) {
    return `Telnyx registration failed (${context.gatewayState})`;
  }

  return context?.socketClose ?? context?.socketError ?? null;
}

function parseGatewayState(message: unknown) {
  if (!message || typeof message !== 'object') return null;

  const typedMessage = message as {
    result?: { params?: { state?: string } };
    params?: { state?: string };
  };

  return typedMessage.result?.params?.state ?? typedMessage.params?.state ?? null;
}

function formatSocketClose(event: unknown) {
  if (!event || typeof event !== 'object') return null;

  const closeEvent = event as {
    code?: number;
    reason?: string;
    wasClean?: boolean;
  };

  if (typeof closeEvent.code !== 'number' && !closeEvent.reason) return null;

  const parts = [`Telnyx socket closed`];
  if (typeof closeEvent.code === 'number') parts.push(`code ${closeEvent.code}`);
  if (closeEvent.reason) parts.push(closeEvent.reason);
  return parts.join(' ');
}

function compactErrorDetails(details: unknown) {
  if (!details || typeof details !== 'object') return null;
  const typedDetails = details as {
    errorMessage?: string;
    message?: string;
    reason?: string;
    type?: string;
    code?: string | number;
  };

  const primary = typedDetails.errorMessage ?? typedDetails.message ?? typedDetails.reason ?? typedDetails.type;
  if (!primary) return null;
  return typedDetails.code ? `${primary} (${typedDetails.code})` : primary;
}

function serializeUnknownError(error: unknown): unknown {
  if (!error || typeof error !== 'object') return error;

  const direct = Object.fromEntries(
    Object.getOwnPropertyNames(error).map((key) => [key, readProperty(error as Record<string, unknown>, key)]),
  );

  const nestedError = readProperty(error as Record<string, unknown>, 'error');
  if (nestedError && typeof nestedError === 'object') {
    direct.error = Object.fromEntries(
      Object.getOwnPropertyNames(nestedError).map((key) => [
        key,
        readProperty(nestedError as Record<string, unknown>, key),
      ]),
    );
  }

  return Object.keys(direct).length > 0 ? direct : error;
}

function readProperty(source: Record<string, unknown>, key: string): unknown {
  try {
    return source[key];
  } catch {
    return '[unreadable]';
  }
}

function shouldAutoAnswer(customHeaders: Array<{ name: string; value: string }> | undefined) {
  return customHeaders?.some((header) => header.name === 'X-Auto-Answer' && header.value === 'true') ?? false;
}

function getCallControlId(call: TelnyxCall) {
  return call.options?.telnyxCallControlId ?? call.telnyxIDs?.telnyxCallControlId ?? null;
}

function getTelnyxSessionId(call: TelnyxCall) {
  return call.options?.telnyxSessionId ?? call.telnyxIDs?.telnyxSessionId ?? null;
}

function createLocalCallId() {
  return `local-${globalThis.crypto.randomUUID()}`;
}

function buildCallTombstoneKeys(
  telnyxCallId: string | null,
  telnyxSessionId: string | null,
  callControlId: string | null,
) {
  return [
    ...(telnyxCallId ? [`telnyx:${telnyxCallId}`] : []),
    ...(telnyxSessionId ? [`session:${telnyxSessionId}`] : []),
    ...(callControlId ? [`callControl:${callControlId}`] : []),
  ];
}

function pruneExpiredCallTombstones(tombstones: Map<string, number>, now: number) {
  for (const [key, expiresAt] of tombstones.entries()) {
    if (expiresAt <= now) {
      tombstones.delete(key);
    }
  }
}

function findLifecycleMatch(
  lifecycleMap: Map<string, CallLifecycleState>,
  telnyxCallId: string | null,
  telnyxSessionId: string | null,
  callControlId: string | null,
  queueEntryId: string | null,
) {
  if (telnyxCallId) {
    for (const [candidateId, lifecycle] of lifecycleMap.entries()) {
      if (lifecycle.telnyxCallId === telnyxCallId) {
        return { callId: candidateId, lifecycle };
      }
    }
  }

  if (telnyxSessionId) {
    for (const [candidateId, lifecycle] of lifecycleMap.entries()) {
      if (lifecycle.telnyxSessionId === telnyxSessionId) {
        return { callId: candidateId, lifecycle };
      }
    }
  }

  if (callControlId) {
    for (const [candidateId, lifecycle] of lifecycleMap.entries()) {
      if (lifecycle.callControlId === callControlId) {
        return { callId: candidateId, lifecycle };
      }
    }
  }

  if (queueEntryId) return null;

  for (const [candidateId, lifecycle] of lifecycleMap.entries()) {
    if (
      candidateId.startsWith('local-') &&
      !lifecycle.queueEntryId &&
      !lifecycle.callControlId &&
      !lifecycle.telnyxCallId &&
      !lifecycle.telnyxSessionId
    ) {
      return { callId: candidateId, lifecycle };
    }
  }

  return null;
}
